/**
 * The elements of a filter.
 * Allows new elements to be added without having to rewrite all old filters in Vespa
 * @module filterTemplate
 * @category Lib
 */

import {
	SDK_Align,
	SDK_ColorChoice,
	type SDK_Filter,
	SDK_FontChoice,
	SDK_FontSizeChoice,
	SDK_FontStyle,
	SDK_FontWeight,
	SDK_StartingPoint,
	SDK_TextDecoration,
} from "../gql/generated/operations.js";
import { strings } from "./strings.js";

export const filterTemplate: SDK_Filter = {
	align: SDK_Align.Center,
	autospace: true,
	background: SDK_ColorChoice.Second,
	borderBlockEnd: 0,
	borderBlockStart: 0,
	borderColor: SDK_ColorChoice.Unset,
	borderInlineEnd: 0,
	borderInlineStart: 0,
	color: SDK_ColorChoice.First,
	cornerRadius: 0,
	decoration: SDK_TextDecoration.Unset,
	filterName: strings.defaultFilter,
	font: SDK_FontChoice.First,
	fontSize: SDK_FontSizeChoice.First,
	fontStyle: SDK_FontStyle.Unset,
	lineHeightMultiplier: 1,
	marginBottomMultiplier: 1,
	marginTopMultiplier: 1,
	ownerName: "quickpoint",
	paddingBlockEnd: 8,
	paddingInlineStart: 8,
	paddingInlineEnd: 8,
	paddingBlockStart: 8,
	publish: true,
	startingPoint: SDK_StartingPoint.MiddleOut,
	weight: SDK_FontWeight.Normal,
	audioPlayer: {
		align: SDK_Align.Unset,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.First,
		fontSize: SDK_FontSizeChoice.Second,
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 1.42,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		weight: SDK_FontWeight.Unset,
	},
	blank: {
		fontSize: SDK_FontSizeChoice.Second,
		leading: 0.42,
	},
	box: {
		align: SDK_Align.Unset,
		background: SDK_ColorChoice.Seventh,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.Unset,
		cornerRadius: 1,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.First,
		fontSize: SDK_FontSizeChoice.Second,
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 0,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 1,
		paddingInlineStart: 1,
		paddingInlineEnd: 1,
		paddingBlockStart: 1,
		weight: SDK_FontWeight.Unset,
	},
	bullet: {
		align: SDK_Align.Unset,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.Second,
		fontSize: SDK_FontSizeChoice.Second,
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 0,
		markerColor: SDK_ColorChoice.Unset,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		space: 0,
		weight: SDK_FontWeight.Unset,
	},
	button: {
		align: SDK_Align.Unset,
		background: SDK_ColorChoice.Second,
		borderBlockEnd: 0.1,
		borderInlineEnd: 0.1,
		borderInlineStart: 0.1,
		borderBlockStart: 0.1,
		borderColor: SDK_ColorChoice.First,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.Second,
		fontSize: SDK_FontSizeChoice.First,
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 1,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 1,
		paddingInlineStart: 1,
		paddingInlineEnd: 1,
		paddingBlockStart: 1,
		weight: SDK_FontWeight.Unset,
	},
	caption: {
		align: SDK_Align.End,
		backdropOpacity: 50,
		background: SDK_ColorChoice.Second,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.First,
		fontSize: SDK_FontSizeChoice.Sixth,
		fontStyle: SDK_FontStyle.Italic,
		lineHeight: 0,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 0.2,
		paddingInlineStart: 0.2,
		paddingInlineEnd: 0.4,
		paddingBlockStart: 0.2,
		weight: SDK_FontWeight.Unset,
	},
	cell: {
		align: SDK_Align.Start,
		startingPoint: SDK_StartingPoint.MiddleOut,
		paddingBlockEnd: 2.0,
		paddingBlockStart: 2.0,
		paddingInlineEnd: 2.0,
		paddingInlineStart: 2.0,
	},
	chart: {
		/* Series hues for multi-series charts; a single series always draws
		   in the filter ink (colors.first) per Tufte. Validated on the cream
		   surface: worst adjacent CVD ΔE 12.9; yellow/aqua/magenta are
		   sub-3:1 so identity always also rides legends, labels, and the
		   data table — never color alone. */
		colors: ["#dc2828", "#2a78d6", "#eda100", "#4a3aa7", "#1baf7a", "#e87ba4"],
	},

	colors: {
		first: "#111111", // Text (Tufte black)
		second: "#fffff8", // Background (Tufte cream)
		third: "#dc2828", // Tufte red (danger/accent)
		fourth: "#fdf4fc", // Unused/Extra
		seventh: "#f6f1e4", // Container tint — box: default background (17:1 under first)
	},
	columnTitle: {
		align: SDK_Align.Center,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.First,
		fontSize: SDK_FontSizeChoice.Second,
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 1.3,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		weight: SDK_FontWeight.Unset,
	},

	fonts: {
		first: "et-book-roman-line-figures.woff",
		second: "et-book-display-italic-old-style-figures.woff",
		third: "et-book-bold-line-figures.woff",
		fourth: "et-book-roman-old-style-figures.woff",
	},
	fontSizes: {
		first: 3.14, // Caption 1.1rem (relative to 1.4rem body)
		second: 4.0, // Body 1.4rem
		third: 4.86, // H3/Subtitle 1.7rem
		fourth: 6.29, // H2/Title 2.2rem
		fifth: 9.14, // H1/Supertitle 3.2rem
		sixth: 2.86, // Code 1.0rem
	},
	frame: {
		paddingBlockEnd: 2,
		paddingBlockStart: 2,
		paddingInlineEnd: 2,
		paddingInlineStart: 2,
	},
	grid: {
		align: SDK_Align.Center,
		columnGap: 1,
		paddingBlockEnd: 1,
		paddingBlockStart: 1,
		paddingInlineEnd: 1,
		paddingInlineStart: 1,
		rowGap: 1,
		singleColumnInPortrait: true,
		startingPoint: SDK_StartingPoint.MiddleOut,
	},
	image: {
		align: SDK_Align.Center,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		cornerRadius: 1,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		paddingBlockEnd: 0,
		opacity: 100,
		paddingBlockStart: 0,
		paddingInlineEnd: 0,
		paddingInlineStart: 0,
	},
	line: {
		align: SDK_Align.Unset,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.First,
		fontSize: SDK_FontSizeChoice.Second,
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 0,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		weight: SDK_FontWeight.Unset,
	},
	link: {
		align: SDK_Align.Unset,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Underline,
		font: SDK_FontChoice.First,
		fontSize: SDK_FontSizeChoice.Second,
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 0,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		visited: SDK_ColorChoice.First,
		weight: SDK_FontWeight.Unset,
	},
	list: {
		align: SDK_Align.Unset,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.First,
		fontSize: SDK_FontSizeChoice.Second,
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 1.43,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		weight: SDK_FontWeight.Unset,
	},
	paragraph: {
		align: SDK_Align.Center,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.First,
		fontSize: SDK_FontSizeChoice.Second,
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 1.43,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		weight: SDK_FontWeight.Unset,
	},

	read: {
		align: SDK_Align.Unset,
		background: SDK_ColorChoice.Second,
		borderBlockEnd: 0.2,
		borderInlineEnd: 0.2,
		borderInlineStart: 0.2,
		borderBlockStart: 0.2,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 2,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.First,
		fontStyle: SDK_FontStyle.Unset,
		fontSize: SDK_FontSizeChoice.First,
		lineHeight: 1.43,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		weight: SDK_FontWeight.Unset,
	},
	stickyFooter: {
		align: SDK_Align.Unset,
		background: SDK_ColorChoice.Second,
		borderBlockEnd: 0.0,
		borderBlockStart: 0.0,
		borderColor: SDK_ColorChoice.Unset,
		borderInlineEnd: 0.0,
		borderInlineStart: 0.0,
		cornerRadius: 0.0,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100.0,
		startingPoint: SDK_StartingPoint.MiddleOut,
	},
	stickyHeader: {
		align: SDK_Align.Unset,
		background: SDK_ColorChoice.Second,
		borderBlockEnd: 0.0,
		borderBlockStart: 0.0,
		borderColor: SDK_ColorChoice.Unset,
		borderInlineEnd: 0.0,
		borderInlineStart: 0.0,
		cornerRadius: 0.0,
		marginBottomMultiplier: 1,
		marginTopMultiplier: 1,
		opacity: 100.0,
		startingPoint: SDK_StartingPoint.MiddleOut,
	},
	subtitle: {
		align: SDK_Align.Center,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset, // Tufte doesn't have a border here usually
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.Second, // Italic
		fontSize: SDK_FontSizeChoice.Third, // H3 size
		fontStyle: SDK_FontStyle.Italic,
		lineHeight: 1,
		marginBottomMultiplier: 1, // Adjusted from 0.56 to match Tufte (1.4rem margin vs 1.4rem lineheight approx)
		marginTopMultiplier: 1.4, // Tufte H3 top margin is 2rem (approx 1.4 * 1.4)
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		weight: SDK_FontWeight.Unset,
	},
	supertitle: {
		align: SDK_Align.Center,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.First, // H1 is normal Roman in Tufte CSS
		fontSize: SDK_FontSizeChoice.Fifth, // H1 size
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 1,
		marginBottomMultiplier: 1.1, // 1.5rem / 1.4rem
		marginTopMultiplier: 2.8, // 4rem / 1.4rem
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		weight: SDK_FontWeight.Unset,
	},
	tableCell: {
		align: SDK_Align.Start,
		startingPoint: SDK_StartingPoint.MiddleOut,
		paddingBlockEnd: 2.0,
		paddingBlockStart: 2.0,
		paddingInlineEnd: 2.0,
		paddingInlineStart: 2.0,
	},
	title: {
		align: SDK_Align.Center,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		color: SDK_ColorChoice.First,
		cornerRadius: 0,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.Second, // H2 is Italic in Tufte CSS
		fontSize: SDK_FontSizeChoice.Fourth, // H2 size
		fontStyle: SDK_FontStyle.Italic,
		lineHeight: 1,
		marginBottomMultiplier: 1, // 1.4rem / 1.4rem
		marginTopMultiplier: 1.5, // 2.1rem / 1.4rem
		opacity: 100,
		paddingBlockEnd: 0,
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
		paddingBlockStart: 0,
		weight: SDK_FontWeight.Unset,
	},
	table: {
		align: SDK_Align.End,
		columnGap: 1,
		paddingBlockEnd: 1,
		paddingBlockStart: 1,
		paddingInlineEnd: 1,
		paddingInlineStart: 1,
		rowGap: 1,
		singleColumnInPortrait: true,
		startingPoint: SDK_StartingPoint.TopDown,
		background: SDK_ColorChoice.Unset,
		borderBlockEnd: 0,
		borderInlineEnd: 0,
		borderInlineStart: 0,
		borderBlockStart: 0,
		borderColor: SDK_ColorChoice.Unset,
		cornerRadius: 0,
		marginTopMultiplier: 1,
		marginBottomMultiplier: 1,
		opacity: 100,
		color: SDK_ColorChoice.First,
		decoration: SDK_TextDecoration.Unset,
		font: SDK_FontChoice.First,
		fontSize: SDK_FontSizeChoice.First,
		fontStyle: SDK_FontStyle.Unset,
		lineHeight: 1.42,
		weight: SDK_FontWeight.Unset,
	},
};

export const systemFonts = [
	"Arial Black",
	"Arial",
	"Baskerville",
	"Courier",
	"Garamond",
	"Georgia",
	"Gill Sans",
	"Helvetica",
	"Impact",
	"Monaco",
	"Palatino",
	"Tahoma",
	"Times New Roman",
	"Trebuchet MS",
	"Verdana",
];
